import React, { useMemo, useEffect } from 'react';
import { Container, Box } from '@chakra-ui/react';
import { motion } from 'framer-motion';

// SubNav variants
import Dropdown from './variants/Dropdown';
import Posts from './variants/Posts';
import Gallery from './variants/Gallery';
import Collection from './variants/Collection';

interface Props {
  activeItem: {
    title?: string;
    menuType?: string;
    _key?: string;
    isGallery?: boolean;
    items?: {
      title?: string;
    }[];
  };
  isExpanded: boolean;
  atTopOfPage: boolean;
  bannerShowing: boolean;
}

export default function NavBarContent({
  activeItem,
  isExpanded,
  atTopOfPage,
  bannerShowing,
}: Readonly<Props>) {
  const hasWindow = typeof window !== 'undefined';
  useEffect(() => {
    document.body.style.overflow = isExpanded ? 'hidden' : 'unset';
  }, [hasWindow, isExpanded]);

  const RenderSubNav = useMemo(() => {
    const isGallery = activeItem?.isGallery;
    const isCollection = activeItem?.menuType === 'collection';
    const isDropdown = activeItem?.menuType === 'dropdown';
    const isPosts = activeItem?.menuType === 'posts';

    if (isGallery) return <Gallery activeItem={activeItem} />;
    if (isCollection) return <Collection activeItem={activeItem} />;
    if (isDropdown) return <Dropdown activeItem={activeItem} />;
    if (isPosts) return <Posts activeItem={activeItem} />;
    return null;
  }, [activeItem]);

  return (
    <Box
      as={motion.div}
      pos="fixed"
      left="50%"
      transform="translateX(-50%)"
      zIndex={10}
      w="100vw"
      bg="background"
      shadow="md"
      overflow="hidden"
      initial={{
        opacity: 0,
        height: '0px',
        top: bannerShowing ? '120px' : '80px',
      }}
      animate={{
        top: atTopOfPage && bannerShowing ? '120px' : '80px',
        opacity: 1,
        height: isExpanded ? 'auto' : '0px',
        transition: {
          delay: 0.0,
          duration: 0.3,
          top: { duration: 0.1, delay: 0 },
        },
      }}
      exit={{
        opacity: 0,
        height: '0px',
        transition: {
          duration: 0.2,
        },
      }}
      // @ts-expect-error needed
      transition={{ duration: 0.5 }}
    >
      <Container
        h="100%"
        py={10}
        px="0"
        pos="relative"
        cursor="default"
        maxH="calc(100vh - 15rem)"
        overflowY="auto"
      >
        {RenderSubNav}
      </Container>
    </Box>
  );
}
